import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft,
    ArrowUpRight,
    Calendar,
    ExternalLink,
    Loader2,
    MapPin,
    Sparkles,
} from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import { useJoinModal } from "@/components/landing/JoinModalProvider";
import { cn } from "@/lib/utils";
import {
    fetchManualEventsFromPublic,
    formatEventRange,
    pickNextHostingEvent,
    stripMd,
} from "@/lib/lumaEvents";
import AddEventDialog from "@/components/events/AddEventDialog";
import ScrollReveal from "@/components/motion/ScrollReveal";
import HoverLift from "@/components/motion/HoverLift";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const PROFILE_URL = (process.env.REACT_APP_LUMA_PROFILE_URL || "").trim();
/** Public calendar page (lu.ma/… or luma.com/cal-…) — where upcoming events actually show. */
const CALENDAR_URL = (process.env.REACT_APP_LUMA_CALENDAR_URL || "").trim();

/**
 * Event sources (first match wins):
 * 1. /manual-events.json in public/
 * 2. REACT_APP_LUMA_PROFILE_URL and/or REACT_APP_LUMA_CALENDAR_URL (no API key)
 * 3. REACT_APP_BACKEND_URL + LUMA_API_KEY on server — optional API list
 */

export default function Events() {
    const { open } = useJoinModal();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mode, setMode] = useState("loading");
    const fallbackEmbed = process.env.REACT_APP_LUMA_FALLBACK_EMBED_URL || "";

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const parsed = await fetchManualEventsFromPublic();
            if (parsed.length > 0) {
                setEvents(parsed);
                setMode("manual");
                setLoading(false);
                return;
            }
        } catch {
            /* no manual file or invalid */
        }

        if (PROFILE_URL || CALENDAR_URL) {
            setEvents([]);
            setMode("profile");
            setLoading(false);
            return;
        }

        if (BACKEND) {
            try {
                const res = await fetch(`${BACKEND}/api/luma/events`);
                const data = await res.json();
                if (!res.ok) {
                    throw new Error(data.detail || "Could not load events.");
                }
                setEvents(Array.isArray(data.events) ? data.events : []);
                setMode("api");
                if (data.message && data.configured === false) {
                    setError(data.message);
                }
            } catch (e) {
                setError(e.message || "Failed to load events from API.");
                setEvents([]);
                setMode("api");
            }
            setLoading(false);
            return;
        }

        setError(
            "Set REACT_APP_LUMA_PROFILE_URL (your Luma profile) or add events to public/manual-events.json.",
        );
        setMode("none");
        setLoading(false);
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const nextHosting = useMemo(
        () => pickNextHostingEvent(events),
        [events],
    );

    const showEmbedFallback =
        Boolean(fallbackEmbed) &&
        !loading &&
        events.length === 0 &&
        mode !== "manual" &&
        mode !== "profile";

    const subcopy =
        mode === "manual"
            ? "Events below are listed in manual-events.json — update that file (or the copy on your server) when you add sessions on Luma."
            : mode === "profile"
              ? "Upcoming sessions live on your Luma calendar page — the user profile mostly shows stats & past activity. Use the buttons below (set your calendar URL in .env for the clearest path)."
              : "Pulled from your Luma calendar when the API is configured. Register here, then finish on Luma.";

    return (
        <main
            data-testid="events-page"
            className="min-h-screen bg-[#FAFAFA] text-zinc-950 overflow-x-hidden"
        >
            <Header />
            <section className="pt-28 md:pt-36 pb-16 md:pb-24">
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-zinc-950 mb-10 transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to home
                    </Link>

                    <div className="flex flex-wrap items-start justify-between gap-6 mb-12 md:mb-16">
                        <div className="max-w-2xl">
                            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-5">
                                <span className="h-px w-8 bg-zinc-400" />
                                Events
                            </div>
                            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-black tracking-[-0.03em] text-zinc-950 leading-[0.95]">
                                Upcoming &amp; live{" "}
                                <span className="text-[#E54D2E]">sessions</span>
                            </h1>
                            <p className="mt-5 max-w-xl text-base text-zinc-600 leading-relaxed">
                                {subcopy}
                            </p>
                        </div>
                        {!loading ? (
                            <AddEventDialog onSuccess={load} />
                        ) : null}
                    </div>

                    {loading && (
                        <div className="flex items-center justify-center py-24 text-zinc-500 gap-3">
                            <Loader2 className="h-6 w-6 animate-spin" />
                            Loading…
                        </div>
                    )}

                    {!loading && error && mode === "none" && (
                        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-6 py-5 text-amber-950 text-sm max-w-2xl">
                            {error}
                        </div>
                    )}

                    {!loading && error && mode === "api" && !showEmbedFallback && (
                        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-6 py-5 text-amber-950 text-sm max-w-2xl mb-8">
                            {error}
                        </div>
                    )}

                    {!loading && mode === "profile" && (PROFILE_URL || CALENDAR_URL) && (
                        <div className="mb-10 space-y-8 max-w-3xl">
                            {fallbackEmbed ? (
                                <div className="space-y-3">
                                    <p className="text-sm font-medium text-zinc-700">
                                        Calendar preview (optional embed)
                                    </p>
                                    <div className="aspect-[16/10] min-h-[420px] w-full overflow-hidden rounded-2xl border border-black/10 bg-white shadow-inner">
                                        <iframe
                                            title="Luma calendar embed"
                                            src={fallbackEmbed}
                                            className="h-full w-full border-0"
                                            loading="lazy"
                                        />
                                    </div>
                                </div>
                            ) : null}

                            <div className="rounded-3xl border border-black/[0.06] bg-white p-8 md:p-10 shadow-lg shadow-zinc-950/5">
                                <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-zinc-950 text-white font-display text-lg font-black">
                                    L
                                </div>
                                <h2 className="mt-6 font-display text-2xl font-bold tracking-tight text-zinc-950">
                                    Find upcoming events on Luma
                                </h2>
                                <p className="mt-3 text-zinc-600 leading-relaxed">
                                    The{" "}
                                    <strong className="text-zinc-800">
                                        user profile
                                    </strong>{" "}
                                    (<span className="whitespace-nowrap">luma.com/user/…</span>)
                                    shows who you are and past activity;{" "}
                                    <strong className="text-zinc-800">
                                        upcoming listings
                                    </strong>{" "}
                                    are on your{" "}
                                    <strong className="text-zinc-800">
                                        calendar
                                    </strong>{" "}
                                    page. In Luma:{" "}
                                    <strong>Calendars</strong> → open your
                                    calendar → <strong>Share</strong> /{" "}
                                    <strong>Copy link</strong> — paste that URL
                                    into{" "}
                                    <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded">
                                        REACT_APP_LUMA_CALENDAR_URL
                                    </code>{" "}
                                    and rebuild.
                                </p>

                                <div className="mt-8 flex flex-wrap gap-3">
                                    {CALENDAR_URL ? (
                                        <a
                                            href={CALENDAR_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 h-14 px-8 rounded-full bg-[#E54D2E] text-white text-base font-medium hover:bg-zinc-950 transition-colors"
                                        >
                                            View upcoming calendar
                                            <ExternalLink className="h-4 w-4" />
                                        </a>
                                    ) : (
                                        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 max-w-xl">
                                            Add{" "}
                                            <code className="text-[11px] bg-white/80 px-1 rounded">
                                                REACT_APP_LUMA_CALENDAR_URL
                                            </code>{" "}
                                            in{" "}
                                            <code className="text-[11px]">
                                                .env
                                            </code>{" "}
                                            with your calendar&apos;s public link
                                            so this button appears.
                                        </div>
                                    )}
                                    {PROFILE_URL ? (
                                        <a
                                            href={PROFILE_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 h-14 px-8 rounded-full border border-black/15 bg-white text-zinc-900 text-base font-medium hover:bg-black/[0.03] transition-colors"
                                        >
                                            Open profile
                                            <ExternalLink className="h-4 w-4" />
                                        </a>
                                    ) : null}
                                </div>

                                <ul className="mt-8 text-sm text-zinc-600 space-y-2 list-disc pl-5 border-t border-black/[0.06] pt-8">
                                    <li>
                                        Each session must be{" "}
                                        <strong>Published</strong> (not draft).
                                    </li>
                                    <li>
                                        Visibility should allow guests to see it (
                                        <strong>Public</strong> or invite link).
                                    </li>
                                    <li>
                                        If you only see past &quot;Hosted&quot;
                                        events, switch to the calendar page or
                                        check the event&apos;s date is still in
                                        the future.
                                    </li>
                                </ul>

                                <p className="mt-6 text-xs text-zinc-500 leading-relaxed">
                                    Optional: list events on this site without an
                                    API by editing{" "}
                                    <code className="bg-zinc-100 px-1.5 py-0.5 rounded text-[11px]">
                                        public/manual-events.json
                                    </code>
                                    .
                                </p>
                            </div>
                        </div>
                    )}

                    {!loading && nextHosting && events.length > 0 && (
                        <div
                            className="mb-10 rounded-3xl border-2 border-[#E54D2E]/35 bg-gradient-to-br from-[#E54D2E]/[0.12] via-white to-white p-6 md:p-8 shadow-lg shadow-[#E54D2E]/10"
                            data-testid="next-hosting-spotlight"
                        >
                            <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#E54D2E]">
                                Next I&apos;m hosting
                            </p>
                            <h2 className="mt-2 font-display text-2xl md:text-3xl font-black tracking-tight text-zinc-950">
                                {nextHosting.name}
                            </h2>
                            <p className="mt-2 text-sm md:text-base text-zinc-600">
                                {formatEventRange(nextHosting)}
                            </p>
                            <div className="mt-6 flex flex-wrap gap-3">
                                <button
                                    type="button"
                                    onClick={() =>
                                        open({
                                            lumaEventUrl: nextHosting.url,
                                            lumaEventName: nextHosting.name,
                                        })
                                    }
                                    className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-zinc-950 text-white text-sm font-medium hover:bg-[#E54D2E] transition-colors"
                                >
                                    Register with Build Bout
                                    <Sparkles className="h-4 w-4" />
                                </button>
                                <a
                                    href={nextHosting.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-black/15 bg-white text-sm font-medium text-zinc-900 hover:bg-black/[0.03] transition-colors"
                                >
                                    RSVP on Luma
                                    <ArrowUpRight className="h-4 w-4" />
                                </a>
                            </div>
                        </div>
                    )}

                    {!loading && events.length > 0 && (
                        <ScrollReveal>
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                            {events.map((ev) => (
                                <li key={ev.id || ev.url}>
                                    <HoverLift className="h-full">
                                    <article className="group h-full flex flex-col overflow-hidden rounded-3xl border border-black/[0.06] bg-white shadow-lg shadow-zinc-950/5 motion-card">
                                        {ev.cover_url ? (
                                            <div className="aspect-[21/9] overflow-hidden bg-zinc-100">
                                                <img
                                                    src={ev.cover_url}
                                                    alt=""
                                                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                                                />
                                            </div>
                                        ) : (
                                            <div className="aspect-[21/9] bg-gradient-to-br from-zinc-900 to-zinc-700 flex items-center justify-center">
                                                <Calendar className="h-12 w-12 text-white/40" />
                                            </div>
                                        )}
                                        <div className="flex flex-1 flex-col p-6 md:p-8">
                                            <time
                                                dateTime={ev.start_at || ""}
                                                className="text-xs font-mono uppercase tracking-[0.15em] text-[#E54D2E]"
                                            >
                                                {formatEventRange(ev)}
                                            </time>
                                            <h2 className="mt-3 font-display text-2xl md:text-[1.65rem] font-bold tracking-tight text-zinc-950 leading-snug">
                                                {ev.name}
                                            </h2>
                                            {ev.description ? (
                                                <p className="mt-3 text-sm text-zinc-600 leading-relaxed line-clamp-3">
                                                    {stripMd(ev.description)}
                                                </p>
                                            ) : null}
                                            <div className="mt-6 flex flex-wrap gap-3 mt-auto pt-2 border-t border-black/[0.06]">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        open({
                                                            lumaEventUrl:
                                                                ev.url,
                                                            lumaEventName:
                                                                ev.name,
                                                        })
                                                    }
                                                    className={cn(
                                                        "inline-flex items-center gap-2 h-12 px-6 rounded-full bg-zinc-950 text-white text-sm font-medium hover:bg-[#E54D2E] transition-colors",
                                                    )}
                                                >
                                                    Register for this event
                                                    <Sparkles className="h-4 w-4" />
                                                </button>
                                                <a
                                                    href={ev.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-2 h-12 px-5 rounded-full border border-black/10 text-sm font-medium text-zinc-800 hover:bg-black/5 transition-colors"
                                                >
                                                    Open in Luma
                                                    <ArrowUpRight className="h-4 w-4" />
                                                </a>
                                            </div>
                                        </div>
                                    </article>
                                    </HoverLift>
                                </li>
                            ))}
                        </ul>
                        </ScrollReveal>
                    )}

                    {!loading && showEmbedFallback && fallbackEmbed && (
                        <div className="space-y-4">
                            <p className="text-sm text-zinc-600 max-w-2xl">
                                Calendar embed (optional):{" "}
                                <code className="text-xs bg-zinc-100 px-1.5 py-0.5 rounded">
                                    REACT_APP_LUMA_FALLBACK_EMBED_URL
                                </code>
                            </p>
                            <div className="aspect-[16/10] min-h-[480px] w-full overflow-hidden rounded-2xl border border-black/10 bg-white shadow-inner">
                                <iframe
                                    title="Luma calendar"
                                    src={fallbackEmbed}
                                    className="h-full w-full border-0"
                                    loading="lazy"
                                />
                            </div>
                        </div>
                    )}

                    {!loading &&
                        events.length === 0 &&
                        !error &&
                        mode === "api" &&
                        !showEmbedFallback && (
                            <div className="rounded-2xl border border-black/8 bg-white px-8 py-16 text-center max-w-lg mx-auto">
                                <MapPin className="h-10 w-10 text-zinc-300 mx-auto mb-4" />
                                <p className="text-zinc-600">
                                    No upcoming events from the API yet. Check
                                    back soon.
                                </p>
                            </div>
                        )}
                </div>
            </section>
            <Footer />
        </main>
    );
}
